import { formatDollars } from '~/domains/common/utils/formatters';

import { BoostType} from '../types';
import type { BonusDefinition, BonusTermsCombined, NormalizedBonus, BoostTypeType, PublicBoost } from '../types';

// ------------------------------------------------------------
// Bonus normalizers and formatting helpers.
// ------------------------------------------------------------
export const formatCentsToDollars = (cents: number): string => {
  if (Number.isNaN(cents)) return '';

  const dollars = cents / 100;
  return dollars % 1 === 0 ? `$${dollars}` : `$${dollars.toFixed(2)}`;
};

export const formatBonusAmountDisplay = (bonus: BonusDefinition): string =>
  bonus.calculationType === 'PERCENTAGE'
    ? `${bonus.percentage}%`
    : formatCentsToDollars(bonus.flatAmountCents ?? bonus.maxBonusCents);

export const normalizeBonus = (bonus: BonusDefinition): NormalizedBonus => {
  const isPercentage = bonus.calculationType === 'PERCENTAGE';
  const minDepositDisplay = formatCentsToDollars(bonus.minDepositCents);

  const terms: BonusTermsCombined = {
    type_title: bonus.terms.type_title,
    type_description: bonus.terms.type_description,
    tags: bonus.terms.tags,
    eligibility: {
      paragraphs: bonus.terms.eligibility?.paragraphs ?? [],
      ordered_list: bonus.terms.eligibility?.ordered_list,
    },
    rules: {
      paragraphs: bonus.terms.rules?.paragraphs ?? [],
      ordered_list: bonus.terms.rules?.ordered_list,
    },
  };

  return {
    promotionType: 'deposit_bonus',
    id: bonus.id,
    title: bonus.title,
    description: bonus.description,
    subtitle: bonus.subtitle,
    bonusAmountDisplay: formatBonusAmountDisplay(bonus),
    minDepositDisplay,
    minDepositCents: bonus.minDepositCents,
    maxBonusCents: bonus.maxBonusCents,
    isPercentage,
    percentage: bonus.percentage,
    expiresAt: bonus.endsAt,
    assignmentExpiresAt: bonus.assignmentExpiresAt,
    isActive: bonus.isActive,
    terms,
    trigger: bonus.trigger,
    original: bonus,
  };
};

export const denormalizeBonus = (bonus: NormalizedBonus): BonusDefinition => {
  const original = bonus.original;

  return {
    ...original,
    id: bonus.id,
    title: bonus.title,
    description: bonus.description,
    subtitle: bonus.subtitle,
    trigger: bonus.trigger ?? original.trigger,
    calculationType: bonus.isPercentage ? 'PERCENTAGE' : 'FLAT',
    percentage: bonus.percentage,
    minDepositCents: bonus.minDepositCents,
    maxBonusCents: bonus.maxBonusCents,
    endsAt: bonus.expiresAt,
    assignmentExpiresAt: bonus.assignmentExpiresAt,
    isActive: bonus.isActive,
    terms: {
      type_title: bonus.terms.type_title,
      type_description: bonus.terms.type_description,
      tags: bonus.terms.tags,
      eligibility: {
        paragraphs: bonus.terms.eligibility.paragraphs,
        ordered_list: bonus.terms.eligibility.ordered_list,
      },
      rules: {
        paragraphs: bonus.terms.rules.paragraphs,
        ordered_list: bonus.terms.rules.ordered_list,
      },
    },
  };
};

// ------------------------------------------------------------
// Boost formatting helpers.
// ------------------------------------------------------------
export const formatBoostValue = (
  boost: Pick<PublicBoost, 'type' | 'payout_type' | 'amount'>
): string | null => {
  if (boost.type === 'risk_free') return null;
  if (boost.payout_type === 'percentage') return `${Math.round(boost.amount * 100)}%`;
  if (boost.payout_type === 'fixed') return formatDollars(boost.amount);
  return null;
};

// ------------------------------------------------------------
// Voucher formatting helpers.
// ------------------------------------------------------------
export const formatBoostCategoryToDisplay = (
  boostType: 'payout_boost' | 'risk_free',
): BoostTypeType => (boostType === 'risk_free' ? BoostType.NO_SWEAT : BoostType.PAYOUT);

export const formatVoucherValue = (maxValue: number): string =>
  formatDollars(Math.round(maxValue) / 100);
